import { Box, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectItem } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'

import { textStyleKeys } from '@mangovoice/design-system'

import { PortaledDropdownSelect } from './PortaledDropdownSelect'

type AssignedUserDropdownProps = {
  users?: { id: number; full_name?: string | null }[]
  selectedUserId?: number | null
  onChange?: (userId: number | null) => void
  isLoading?: boolean
}

const UNASSIGNED_OPTION_VALUE = 'unassigned'

export const AssignedUserDropdown = ({
  users = [],
  selectedUserId,
  onChange,
  isLoading,
}: AssignedUserDropdownProps) => {
  const { t } = useTranslation()
  const items: SelectItem<string>[] = useMemo(() => {
    const loadingLabel = (
      <Text
        lineClamp={1}
        textStyle={textStyleKeys.Body2}
        color="fg.muted"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {t('missedCalls.assignedUser.loading', 'Loading...')}
      </Text>
    )

    const unassignedLabel = (
      <Text
        lineClamp={1}
        textStyle={textStyleKeys.Body2}
        color="fg.default"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {t('missedCalls.assignedUser.unassigned', 'Unassigned')}
      </Text>
    )

    const baseItems: SelectItem<string>[] = [
      {
        value: UNASSIGNED_OPTION_VALUE,
        selectedLabel: unassignedLabel,
        content: (
          <MenuListItem
            primaryText={t('missedCalls.assignedUser.unassigned', 'Unassigned')}
          />
        ),
        searchLabel: t('missedCalls.assignedUser.unassigned', 'Unassigned'),
      },
    ]

    if (isLoading) {
      return [
        ...baseItems,
        {
          value: '__loading__',
          selectedLabel: loadingLabel,
          content: (
            <MenuListItem
              primaryText={t('missedCalls.assignedUser.loading', 'Loading...')}
            />
          ),
          clickDisabled: true,
          searchLabel: t('missedCalls.assignedUser.loadingShort', 'Loading'),
        },
      ]
    }

    const userItems = users.map<SelectItem<string>>((user) => {
      const displayName =
        user.full_name?.trim() && user.full_name.length > 0
          ? user.full_name
          : t('missedCalls.assignedUser.userFallback', 'User {{id}}', {
              id: user.id,
            })

      return {
        value: user.id.toString(),
        selectedLabel: (
          <Text
            lineClamp={1}
            textStyle={textStyleKeys.Body2}
            color="fg.default"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {displayName}
          </Text>
        ),
        content: <MenuListItem primaryText={displayName} />,
        searchLabel: displayName,
      }
    })

    const combinedItems = [...baseItems, ...userItems]

    if (
      !isLoading &&
      selectedUserId !== null &&
      selectedUserId !== undefined &&
      !users.some((user) => user.id === selectedUserId)
    ) {
      const fallbackLabel = t(
        'missedCalls.assignedUser.userFallback',
        'User {{id}}',
        { id: selectedUserId },
      )
      combinedItems.push({
        value: selectedUserId.toString(),
        selectedLabel: (
          <Text
            lineClamp={1}
            textStyle={textStyleKeys.Body2}
            color="fg.default"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {fallbackLabel}
          </Text>
        ),
        content: <MenuListItem primaryText={fallbackLabel} />,
        searchLabel: fallbackLabel,
      })
    }

    return combinedItems
  }, [users, isLoading, selectedUserId, t])

  const handleSelect = (value: string) => {
    if (value === UNASSIGNED_OPTION_VALUE) {
      onChange?.(null)
      return
    }

    const numericUserId = Number(value)
    if (Number.isNaN(numericUserId)) {
      return
    }

    onChange?.(numericUserId)
  }

  const resolvedSelectedValue =
    selectedUserId === null || selectedUserId === undefined
      ? UNASSIGNED_OPTION_VALUE
      : selectedUserId.toString()

  return (
    <Box width="90%" minW={0}>
      <PortaledDropdownSelect<string>
        placeholder={t('form.placeholder.selectUser')}
        items={items}
        selectedValue={resolvedSelectedValue}
        onSelect={(newValue) => handleSelect(newValue)}
        searchable
        menuOptions={{
          width: '18rem',
        }}
      />
    </Box>
  )
}

export default AssignedUserDropdown
